import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, BadgeAlert, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function currentMonth() {
  return String(new Date().getMonth() + 1);
}

function currentYear() {
  return String(new Date().getFullYear());
}

function StatusBadge({ type }) {
  const classes = type === 'income' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800';
  return <Badge className={classes}>{type}</Badge>;
}

export default function BudgetTransactions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    month: currentMonth(),
    year: currentYear(),
    branch_id: 'all',
    currency: 'all',
    type: 'all',
    category_id: 'all',
    search: '',
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    type: 'expense',
    branch_id: 'all',
    category_id: '',
    amount: '',
    currency: user?.default_currency || 'UGX',
    transaction_date: new Date().toISOString().slice(0, 16),
    description: '',
    payment_method: 'cash',
    reference: '',
  });

  const load = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = { month: nextFilters.month, year: nextFilters.year };
      if (nextFilters.branch_id && nextFilters.branch_id !== 'all') params.branch_id = nextFilters.branch_id;
      if (nextFilters.currency && nextFilters.currency !== 'all') params.currency = nextFilters.currency;
      if (nextFilters.type && nextFilters.type !== 'all') params.type = nextFilters.type;
      if (nextFilters.category_id && nextFilters.category_id !== 'all') params.category_id = nextFilters.category_id;
      if (nextFilters.search) params.search = nextFilters.search;
      const res = await api.get('/budget/transactions', { params });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load budget transactions');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branches = useMemo(() => Array.isArray(data?.branches) ? data.branches : [], [data?.branches]);
  const categories = useMemo(() => Array.isArray(data?.categories) ? data.categories : [], [data?.categories]);
  const activeBranches = useMemo(() => branches.filter((branch) => String(branch?.status || 'active').toLowerCase() === 'active'), [branches]);
  const currencyOptions = useMemo(() => Array.isArray(data?.filters?.currencies) ? data.filters.currencies : [], [data?.filters?.currencies]);
  const singleBranch = activeBranches.length <= 1;
  const currentCurrency = user?.default_currency || data?.summary?.currency || 'UGX';
  const displayCurrency = filters.currency && filters.currency !== 'all' ? filters.currency : currentCurrency;
  const transactions = Array.isArray(data?.transactions) ? data.transactions : [];
  const warnings = Array.isArray(data?.warnings) ? data.warnings : [];

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    load(next);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      type: 'expense',
      branch_id: singleBranch ? (activeBranches[0]?.id || branches[0]?.id || 'all') : (filters.branch_id || 'all'),
      category_id: categories.find((c) => c.type === 'expense')?.id || categories[0]?.id || '',
      amount: '',
      currency: user?.default_currency || 'UGX',
      transaction_date: new Date().toISOString().slice(0, 16),
      description: '',
      payment_method: 'cash',
      reference: '',
    });
    setOpen(true);
  };

  const openEdit = (txn) => {
    setEditing(txn);
    setForm({
      type: txn.type || 'expense',
      branch_id: txn.branch_id || (singleBranch ? (activeBranches[0]?.id || branches[0]?.id || 'all') : 'all'),
      category_id: txn.category_id || '',
      amount: txn.amount ?? '',
      currency: txn.currency || user?.default_currency || 'UGX',
      transaction_date: (txn.transaction_date || txn.created_at || new Date().toISOString()).slice(0, 16),
      description: txn.description || '',
      payment_method: txn.payment_method || 'cash',
      reference: txn.reference || '',
    });
    setOpen(true);
  };

  const saveTransaction = async () => {
    if (!form.category_id) {
      toast.error('Please select a category.');
      return;
    }
    if (!singleBranch && !form.branch_id) {
      toast.error('Please choose a branch.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount || 0),
      };
      if (singleBranch) payload.branch_id = activeBranches[0]?.id || branches[0]?.id || payload.branch_id;
      if (editing?.id) {
        const res = await api.put(`/budget/transactions/${editing.id}`, payload);
        if (res.data?.warning?.message) toast.warning(res.data.warning.message);
        toast.success(res.data?.message || 'Transaction updated');
      } else {
        const res = await api.post('/budget/transactions', payload);
        if (res.data?.warning?.message) toast.warning(res.data.warning.message);
        toast.success(res.data?.message || 'Transaction saved');
      }
      setOpen(false);
      setEditing(null);
      await load(filters);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Could not save transaction');
    } finally {
      setSaving(false);
    }
  };

  const deleteTransaction = async (txn) => {
    if (!window.confirm('Delete this transaction? This will recalculate running balances.')) return;
    try {
      await api.delete(`/budget/transactions/${txn.id}`);
      toast.success('Transaction deleted');
      await load(filters);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Could not delete transaction');
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <ArrowLeftRight size={22} className="text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget Transactions</h1>
              <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
            {data?.message || 'This feature is available on premium packages only.'}
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate('/dashboard/package')}>
              View Packages
            </Button>
            <Button variant="outline" className="border-slate-200" onClick={() => load()}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Budget Transactions
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Track income and expenses with a running balance.</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={openCreate}>
          <Plus size={16} className="mr-1.5" /> Add Transaction
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Current Balance</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(summary.current_balance || 0, displayCurrency)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Total Income This Month</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(summary.total_income_this_month || 0, displayCurrency)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Total Expenses This Month</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(summary.total_expenses_this_month || 0, displayCurrency)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Net Change</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money((summary.total_income_this_month || 0) - (summary.total_expenses_this_month || 0), displayCurrency)}</p>
          </CardContent>
        </Card>
      </div>

      {warnings.length ? (
        <div className="grid gap-3">
          {warnings.map((warning, index) => (
            <Card key={`${warning.title}-${index}`} className={`border rounded-2xl shadow-sm ${warning.type === 'danger' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${warning.type === 'danger' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                  <BadgeAlert size={18} />
                </div>
                <div>
                  <p className={`text-sm font-semibold ${warning.type === 'danger' ? 'text-red-900' : 'text-amber-900'}`}>{warning.title}</p>
                  <p className={`text-sm mt-1 ${warning.type === 'danger' ? 'text-red-800' : 'text-amber-800'}`}>{warning.message}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Month</Label>
              <Select value={filters.month} onValueChange={(value) => updateFilter('month', value)}>
                <SelectTrigger className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, index) => String(index + 1)).map((item) => (
                    <SelectItem key={item} value={item}>{new Date(2025, Number(item) - 1, 1).toLocaleString('en-US', { month: 'long' })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Year</Label>
              <Select value={filters.year} onValueChange={(value) => updateFilter('year', value)}>
                <SelectTrigger className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 6 }, (_, i) => String(Number(currentYear()) - i)).map((value) => (
                    <SelectItem key={value} value={value}>{value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!singleBranch ? (
              <div>
                <Label className="text-xs text-slate-500">Branch</Label>
                <Select value={filters.branch_id} onValueChange={(value) => updateFilter('branch_id', value)}>
                  <SelectTrigger className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-xs text-slate-500">Branch</Label>
                <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">{branches[0]?.name || 'Main Branch'}</div>
              </div>
            )}
            {currencyOptions.length > 1 ? (
              <div>
                <Label className="text-xs text-slate-500">Currency</Label>
                <Select value={filters.currency} onValueChange={(value) => updateFilter('currency', value)}>
                  <SelectTrigger className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Currencies</SelectItem>
                    {currencyOptions.map((currency) => <SelectItem key={currency} value={currency}>{currency}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div>
              <Label className="text-xs text-slate-500">Type</Label>
              <Select value={filters.type} onValueChange={(value) => updateFilter('type', value)}>
                <SelectTrigger className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Category</Label>
              <Select value={filters.category_id} onValueChange={(value) => updateFilter('category_id', value)}>
                <SelectTrigger className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Search</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={filters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  placeholder="Search transactions"
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Transactions</CardTitle>
          <Badge variant="outline" className="border-slate-200 text-slate-600 bg-slate-50">{transactions.length} item(s)</Badge>
        </CardHeader>
        <CardContent>
          {transactions.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-100">
                    <th className="py-3 pr-4 font-medium">Date</th>
                    <th className="py-3 pr-4 font-medium">Branch</th>
                    <th className="py-3 pr-4 font-medium">Type</th>
                    <th className="py-3 pr-4 font-medium">Category</th>
                    <th className="py-3 pr-4 font-medium">Description</th>
                    <th className="py-3 pr-4 font-medium">Amount</th>
                    <th className="py-3 pr-4 font-medium">Running Balance</th>
                    <th className="py-3 pr-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => {
                    const category = categories.find((item) => item.id === txn.category_id);
                    const branch = branches.find((item) => item.id === txn.branch_id);
                    const balance = Number(txn.running_balance || 0);
                    return (
                      <tr key={txn.id} className="border-b border-slate-50 last:border-0">
                        <td className="py-3 pr-4 text-slate-700">{formatDate(txn.transaction_date || txn.created_at)}</td>
                        <td className="py-3 pr-4 text-slate-700">{branch?.name || 'Main Branch'}</td>
                        <td className="py-3 pr-4"><StatusBadge type={txn.type} /></td>
                        <td className="py-3 pr-4 text-slate-700">{category?.name || 'Uncategorized'}</td>
                        <td className="py-3 pr-4 text-slate-700 max-w-[280px] truncate">{txn.description || '-'}</td>
                        <td className="py-3 pr-4 font-medium text-slate-900">{money(txn.amount, txn.currency || displayCurrency)}</td>
                        <td className={`py-3 pr-4 font-semibold mono-nums ${balance >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{money(balance, txn.currency || displayCurrency)}</td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" className="border-slate-200" onClick={() => openEdit(txn)}>Edit</Button>
                            <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteTransaction(txn)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8">No transactions found for this period.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Transaction' : 'Add Transaction'}</DialogTitle>
            <DialogDescription>Record income or expense and keep your running balance up to date.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(value) => setForm((prev) => ({ ...prev, type: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!singleBranch ? (
              <div className="grid gap-2">
                <Label>Branch</Label>
                <Select value={form.branch_id} onValueChange={(value) => setForm((prev) => ({ ...prev, branch_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    {(activeBranches.length ? activeBranches : branches).map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid gap-2">
              <Label>Category</Label>
              <Select value={form.category_id} onValueChange={(value) => setForm((prev) => ({ ...prev, category_id: value }))}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {(categories.filter((c) => !c.type || c.type === form.type || c.type === 'both').length
                    ? categories.filter((c) => !c.type || c.type === form.type || c.type === 'both')
                    : categories
                  ).map((category) => <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Amount</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Currency</Label>
                <Input value={form.currency} onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }))} />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Date</Label>
              <Input type="datetime-local" value={form.transaction_date} onChange={(e) => setForm((prev) => ({ ...prev, transaction_date: e.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="Add a short note" />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(value) => setForm((prev) => ({ ...prev, payment_method: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="jjumapay_online">Jjuma Pay online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Reference</Label>
                <Input value={form.reference} onChange={(e) => setForm((prev) => ({ ...prev, reference: e.target.value }))} placeholder="Optional" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={saveTransaction} disabled={saving}>
              {saving ? 'Saving...' : 'Save Transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
